import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, LayoutGrid, Smartphone, Monitor, FileText, Home } from 'lucide-react';
import { PAPER } from './theme';
import { RolePreviewToggle } from '../../components/RolePreviewToggle';
import { ImpersonationBanner } from '../../components/impersonation/ImpersonationBanner';
import { switchShell } from '../../components/InstallIdentity';

/**
 * DiaryLayout — the shell for every Site Diary screen.
 *
 * Warm-paper theme: cream background with a faint grid, white paper
 * cards, ink-dark text. Header stays sticky with a subtle blur so the
 * timeline can scroll under it without losing context.
 */
export const DiaryLayout = ({ children }) => {
  const { user, logout, isAdmin, isViewingAsUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // DIA-2 §2.5 — the diary lives under BOTH /diary/* (admin desktop)
  // AND /portal/diary/* (portal shell). Navigating to the picker must
  // preserve whichever prefix the user is currently on.
  const inPortalDiary = location.pathname.startsWith('/portal/diary');
  const diaryHome = inPortalDiary ? '/portal/diary' : '/diary';

  // DIA-2 §2.5 — admin-only toggle between the two diary shells. Preserves
  // any /:projectId suffix so an admin editing an entry can flip between
  // "how it looks in the ops app" and "how it looks on a phone" without
  // losing their place. Ops-app-header Layout / PortalLayout wrappers
  // never see diary routes (DiaryLayout wraps both), so the toggle has to
  // live here.
  const toggleDiaryShell = () => {
    const p = location.pathname || '';
    const dest = inPortalDiary ? p.replace('/portal/diary', '/diary') : `/portal${p}`;
    navigate(dest);
    // No full reload — DiaryLayout re-renders and the axios interceptor
    // picks up the new URL prefix on the next request.
  };

  const initials = (user?.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: PAPER.page,
        // Faint dot grid — evokes a physical site diary without stealing focus.
        backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        color: PAPER.ink,
      }}
    >
      <header
        className="sticky top-0 z-30"
        style={{
          background: 'rgba(250,247,242,0.92)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${PAPER.border}`,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <ImpersonationBanner scope="diary" />
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(diaryHome)}
            className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
            data-testid="diary-home-btn"
          >
            <span
              className="w-9 h-9 rounded-lg font-bold text-[15px] flex items-center justify-center flex-shrink-0"
              style={{ background: PAPER.navy, color: PAPER.accent }}
            >
              SD
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold truncate" style={{ color: PAPER.ink }}>Site Diary</span>
              <span className="block text-[11px] -mt-0.5" style={{ color: PAPER.muted }}>Lux Operations</span>
            </span>
          </button>

          {/* Surveyors reach the diary from the Home hub — give them a
              one-tap way back to the main dashboard (admins use "Ops app"). */}
          {!isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/portal')}
              title="Back to dashboard"
              className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
              style={{ background: PAPER.navy, color: PAPER.page }}
              data-testid="diary-back-to-dashboard-btn"
            >
              <Home className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
          )}

          {/* Admins toggle between the diary shells + the full ops app. */}
          {isAdmin && (
            <button
              type="button"
              onClick={toggleDiaryShell}
              title={inPortalDiary ? 'Switch to the admin (desktop) diary view' : 'Switch to the portal (phone) diary view'}
              className="text-xs px-3 py-1.5 rounded-md hidden md:flex items-center gap-1.5 transition-colors hover:bg-black/5"
              style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
              data-testid={inPortalDiary ? 'diary-switch-to-admin-view-btn' : 'diary-switch-to-portal-view-btn'}
            >
              {inPortalDiary ? <Monitor className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">
                {inPortalDiary ? 'Admin view' : 'Portal view'}
              </span>
            </button>
          )}

          {/* DIA-2 §2.4 — Audit report link. Admin only, admin view only
              (the report is a desktop screen at /diary-audit). */}
          {isAdmin && !inPortalDiary && (
            <button
              type="button"
              onClick={() => navigate('/diary-audit')}
              title="View diary audit report"
              className="text-xs px-3 py-1.5 rounded-md hidden md:flex items-center gap-1.5 transition-colors hover:bg-black/5"
              style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
              data-testid="diary-audit-link-btn"
            >
              <FileText className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Audit</span>
            </button>
          )}

          {/* Admins toggle to the full ops app; surveyors don't see this. */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => switchShell(navigate, '/projects')}
              title="Switch to the operations app"
              className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
              style={{ background: PAPER.navy, color: PAPER.page }}
              data-testid="diary-switch-to-ops-btn"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Ops app</span>
            </button>
          )}

          {/* Role Preview dropdown — desktop only. On mobile, admins pick the
              preview role from the ops-app hamburger instead. */}
          <div className="hidden md:inline-block">
            <RolePreviewToggle theme="light" />
          </div>

          <div
            className={`w-9 h-9 rounded-full items-center justify-center text-[12px] font-semibold ${isAdmin ? 'hidden md:flex' : 'flex'}`}
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
            title={user?.name || 'You'}
          >
            {initials}
          </div>

          {!isViewingAsUser && <button
            type="button"
            onClick={logout}
            title="Sign out"
            className={`p-1.5 rounded-md hover:bg-black/5 transition-colors ${isAdmin ? 'hidden md:inline-flex' : 'inline-flex'}`}
            style={{ color: PAPER.muted }}
            data-testid="diary-sign-out-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>}
        </div>
      </header>

      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};
